import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Payment as PaymentIcon,
  Person as UserIcon,
  CreditCard as CreditIcon,
  TrendingUp as UpgradeIcon,
  Refresh as RenewIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const RequestModal = ({ open, onClose, request, onAction }) => {
  if (!request) return null;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      payment_pending: 'info',
      processed: 'primary'
    };
    return colors[status] || 'default';
  };

  const getRequestTypeColor = (type) => {
    const colors = {
      add_user: 'primary',
      add_credits: 'secondary',
      upgrade: 'success',
      renew: 'info'
    };
    return colors[type] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'error',
      medium: 'warning',
      low: 'success'
    };
    return colors[priority] || 'default';
  };

  const getRequestTypeIcon = (type) => {
    const icons = {
      add_user: <UserIcon />,
      add_credits: <CreditIcon />,
      upgrade: <UpgradeIcon />,
      renew: <RenewIcon />
    };
    return icons[type] || <UserIcon />;
  };

  const getRequestTypeDescription = (type) => {
    const descriptions = {
      add_user: 'Add additional users to existing plan',
      add_credits: 'Purchase additional credits',
      upgrade: 'Upgrade to a higher tier plan',
      renew: 'Renew current subscription plan'
    };
    return descriptions[type] || 'Request for service modification';
  };

  const getChangeIndicator = (current, requested) => {
    if (current === requested) return { text: 'No Change', color: 'default' };
    if (requested > current) return { text: `+${requested - current}`, color: 'success' };
    return { text: `${requested - current}`, color: 'error' };
  };

  // Plan-based request validation
  const getCurrentPlanType = (request) => {
    // This should come from the request data - for now using mock logic
    // In real implementation, this would be request.currentPlanType
    if (request.currentUsers <= 5 && request.currentCredits <= 500) return 'free';
    if (request.currentUsers <= 20 && request.currentCredits <= 2000) return 'basic';
    return 'advance';
  };

  const getValidRequestTypes = (currentPlan) => {
    const validRequests = {
      free: ['upgrade'],
      basic: ['upgrade', 'renew', 'add_user'],
      advance: ['renew', 'add_user', 'add_credits']
    };
    return validRequests[currentPlan] || [];
  };

  const isRequestValid = (request) => {
    const currentPlan = getCurrentPlanType(request);
    const validTypes = getValidRequestTypes(currentPlan);
    return validTypes.includes(request.requestType);
  };

  const getPlanUpgradePath = (currentPlan) => {
    const upgradePaths = {
      free: 'Basic Plan',
      basic: 'Advance Plan',
      advance: 'Enterprise Plan'
    };
    return upgradePaths[currentPlan] || 'Higher Tier';
  };

  const getPlanRestrictions = (currentPlan) => {
    const restrictions = {
      free: 'Limited to 5 users and 500 credits',
      basic: 'Up to 20 users and 2,000 credits',
      advance: 'Unlimited users with credit-based usage'
    };
    return restrictions[currentPlan] || '';
  };

  const userChange = getChangeIndicator(request.currentUsers, request.requestedUsers);
  const creditChange = getChangeIndicator(request.currentCredits, request.requestedCredits);
  const currentPlan = getCurrentPlanType(request);
  const isValidRequest = isRequestValid(request);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight="bold">Request Details</Typography>
          <Chip
            label={request.status.replace('_', ' ').toUpperCase()}
            color={getStatusColor(request.status)}
            size="medium"
            sx={{ fontWeight: 'bold' }}
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Plan Validation Alert */}
        {!isValidRequest && (
          <Alert severity="warning" sx={{ mb: 3 }} icon={<InfoIcon />}>
            <Typography variant="body2" fontWeight="bold">
              Invalid Request Type for Current Plan
            </Typography>
            <Typography variant="body2">
              Customer is on {currentPlan.toUpperCase()} plan. Valid request types: {getValidRequestTypes(currentPlan).join(', ').toUpperCase()}
            </Typography>
          </Alert>
        )}

        {/* Request Type Highlight Section */}
        <Card sx={{ mb: 3, bgcolor: isValidRequest ? 'primary.light' : 'warning.light', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                borderRadius: '50%', 
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {getRequestTypeIcon(request.requestType)}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {request.requestType.replace('_', ' ').toUpperCase()} REQUEST
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {getRequestTypeDescription(request.requestType)}
                </Typography>
                {request.requestType === 'upgrade' && (
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    Upgrading from {currentPlan.toUpperCase()} to {getPlanUpgradePath(currentPlan)}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`Current Plan: ${currentPlan.toUpperCase()}`}
                color="default"
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Chip
                label={`Priority: ${request.priority.toUpperCase()}`}
                color={getPriorityColor(request.priority)}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Chip
                label={`Amount: $${request.amount}`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }}
              />
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {/* Customer Information */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
              Customer Information
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Company</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary.main">
                      {request.customerName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Email</Typography>
                    <Typography variant="body1">
                      {request.customerEmail}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Current Plan</Typography>
                    <Chip
                      label={currentPlan.toUpperCase()}
                      color="primary"
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Plan Restrictions</Typography>
                    <Typography variant="body2" color="textSecondary" fontSize="0.75rem">
                      {getPlanRestrictions(currentPlan)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Request Date</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {request.requestDate}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Request Details */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
              Request Details
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Request Description</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {request.requestDetails}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Amount</Typography>
                    <Typography variant="h5" color="primary.main" fontWeight="bold">
                      ${request.amount}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Valid Request Types</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {getValidRequestTypes(currentPlan).map((type) => (
                        <Chip
                          key={type}
                          label={type.replace('_', ' ').toUpperCase()}
                          color={request.requestType === type ? 'success' : 'default'}
                          size="small"
                          variant={request.requestType === type ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Current vs Requested Changes */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
              Requested Changes
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>User Allocation</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Current: {request.currentUsers}</Typography>
                      <Typography variant="h6" color="primary.main" fontWeight="bold">
                        → {request.requestedUsers}
                      </Typography>
                      <Chip
                        label={userChange.text}
                        color={userChange.color}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Credit Allocation</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">{request.currentCredits.toLocaleString()}</Typography>
                      <Typography variant="h6" color="primary.main" fontWeight="bold">
                        → {request.requestedCredits.toLocaleString()}
                      </Typography>
                      <Chip
                        label={creditChange.text}
                        color={creditChange.color}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Detailed Usage Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
              Detailed Usage Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ borderColor: 'primary.main' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <UserIcon color="primary" />
                      <Typography variant="h6" color="primary.main" fontWeight="bold">
                        User Management
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="h4" color="primary.main" fontWeight="bold">
                            {request.currentUsers}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">Current Users</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                          <Typography variant="h4" color="secondary.main" fontWeight="bold">
                            {request.requestedUsers}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">Requested Users</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    {userChange.text !== 'No Change' && (
                      <Alert severity={userChange.color === 'success' ? 'success' : 'info'} sx={{ mt: 2 }}>
                        {userChange.text === '+' + (request.requestedUsers - request.currentUsers) 
                          ? `Adding ${request.requestedUsers - request.currentUsers} new users`
                          : `Reducing users by ${request.currentUsers - request.requestedUsers}`
                        }
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ borderColor: 'secondary.main' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CreditIcon color="secondary" />
                      <Typography variant="h6" color="secondary.main" fontWeight="bold">
                        Credit Management
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="h4" color="primary.main" fontWeight="bold">
                            {request.currentCredits.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">Current Credits</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                          <Typography variant="h4" color="secondary.main" fontWeight="bold">
                            {request.requestedCredits.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">Requested Credits</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    {creditChange.text !== 'No Change' && (
                      <Alert severity={creditChange.color === 'success' ? 'success' : 'info'} sx={{ mt: 2 }}>
                        {creditChange.text === '+' + (request.requestedCredits - request.currentCredits) 
                          ? `Adding ${(request.requestedCredits - request.currentCredits).toLocaleString()} credits`
                          : `Reducing credits by ${(request.currentCredits - request.requestedCredits).toLocaleString()}`
                        }
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {request.status === 'pending' && (
          <>
            <Button 
              onClick={() => onAction(request.id, 'approve')} 
              variant="contained" 
              color="success"
              startIcon={<ApproveIcon />}
              size="large"
              disabled={!isValidRequest}
            >
              Approve Request
            </Button>
            <Button 
              onClick={() => onAction(request.id, 'reject')} 
              variant="contained" 
              color="error"
              startIcon={<RejectIcon />}
              size="large"
            >
              Reject Request
            </Button>
          </>
        )}
        {request.status === 'payment_pending' && (
          <Button 
            onClick={() => onAction(request.id, 'process_payment')} 
            variant="contained" 
            color="primary"
            startIcon={<PaymentIcon />}
            size="large"
          >
            Process Payment
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RequestModal;